import { UniQCommand, UniQChatMessage } from "us.common/interfaces";
import { httpCollection } from "us.helper/http/collection";

// Mock response for demonstration
const mockResponse = {
  id: "msg9",
  responseType: "TEXT",
  responseContent: "Absolutely! Feel free to reach out any time 😊",
  links: [],
  timestamp: new Date("2025-07-08T10:00:35Z"),
  isUser: false,
  isError: false,
};

// Error response for when service fails
const createErrorResponse = (errorMessage: string) => ({
  id: `error-${Date.now()}`,
  responseType: "TEXT",
  responseContent: `Sorry, I'm having trouble processing your request right now. ${errorMessage}.`,
  links: [],
  timestamp: new Date(),
  isUser: false,
  isError: true,
});

// Network error response
const networkErrorResponse = createErrorResponse(
  "It seems there's a connection issue",
);

// Server error response
const serverErrorResponse = createErrorResponse(
  "Our service is temporarily unavailable",
);

export default {
  sendCommand: async (payload: UniQCommand): Promise<any> => {
    return new Promise<{}>(async (resolve, reject) => {
      try {
        const { data, status } = await httpCollection.post(
          "aiservice",
          "ai/command",
          payload,
        );
        if (status === 200) {
          resolve({ data, status });
        } else if (status >= 400 && status < 500) {
          // Client errors (400-499) - resolve with error message
          // Use a generic error message for client errors
          resolve({
            data: createErrorResponse(
              "Please check your request and try again",
            ),
            status: status,
          });
        } else if (status >= 500) {
          // Server errors (500+) - resolve with server error message
          resolve({ data: serverErrorResponse, status: status });
        } else {
          // Other status codes - use mock response
          resolve({ data: mockResponse, status: status });
        }
      } catch (error: any) {
        console.error("UniQ service error:", error);

        // Check if it's a network error
        if (
          error.code === "NETWORK_ERROR" ||
          error.message?.includes("Network Error")
        ) {
          resolve({ data: networkErrorResponse, status: 503 });
        } else if (error.response?.status) {
          // API returned an error with status code
          const status = error.response.status;
          if (status >= 400 && status < 500) {
            resolve({
              data: createErrorResponse(
                "Please check your request and try again",
              ),
              status: status,
            });
          } else if (status >= 500) {
            resolve({ data: serverErrorResponse, status: status });
          } else {
            resolve({
              data: createErrorResponse("An unexpected error occurred"),
              status: status,
            });
          }
        } else {
          // Generic error - provide user-friendly message
          resolve({
            data: createErrorResponse("An unexpected error occurred"),
            status: 500,
          });
        }
      }
    });
  },
  command: {
    send: async (payload: UniQCommand): Promise<any> => {
      return new Promise<{}>(async (resolve, reject) => {
        try {
          const { data, status } = await httpCollection.post(
            "aiservice",
            "ai/command",
            payload,
          );
          if (status === 200) {
            resolve({ data, status });
          } else if (status >= 400 && status < 500) {
            // Client errors (400-499) - resolve with error message
            // Use a generic error message for client errors
            resolve({
              data: createErrorResponse(
                "Please check your request and try again",
              ),
              status: status,
            });
          } else if (status >= 500) {
            // Server errors (500+) - resolve with server error message
            resolve({ data: serverErrorResponse, status: status });
          } else {
            // Other status codes - use mock response
            resolve({ data: mockResponse, status: status });
          }
        } catch (error: any) {
          console.error("UniQ service error:", error);

          // Check if it's a network error
          if (
            error.code === "NETWORK_ERROR" ||
            error.message?.includes("Network Error")
          ) {
            resolve({ data: networkErrorResponse, status: 503 });
          } else if (error.response?.status) {
            // API returned an error with status code
            const status = error.response.status;
            if (status >= 400 && status < 500) {
              resolve({
                data: createErrorResponse(
                  "Please check your request and try again",
                ),
                status: status,
              });
            } else if (status >= 500) {
              resolve({ data: serverErrorResponse, status: status });
            } else {
              resolve({
                data: createErrorResponse("An unexpected error occurred"),
                status: status,
              });
            }
          } else {
            // Generic error - provide user-friendly message
            resolve({
              data: createErrorResponse("An unexpected error occurred"),
              status: 500,
            });
          }
        }
      });
    },
    reset: (): Promise<any> => {
      return new Promise<{}>(async (resolve, reject) => {
        try {
          const { data, status } = await httpCollection.get(
            "aiservice",
            "ai/reset",
            {},
          );
          resolve({ data, status });
        } catch (error) {
          reject(error);
        }
      });
    },
  },
  getDefaultPrompt: ({ caseNo }: any): Promise<any> => {
    return new Promise<{}>(async (resolve, reject) => {
      try {
        const { data, status } = await httpCollection.post(
          "aiservice",
          "ai/case/overview",
          { caseNo },
        );
        resolve({ data, status });
      } catch (error) {
        reject(error);
      }
    });
  },
  saveDefaultPrompt: ({ userName, userAIPrompt }: any): Promise<any> => {
    return new Promise<{}>(async (resolve, reject) => {
      try {
        const { data, status } = await httpCollection.put(
          "aiservice",
          "ai/settings",
          { userName, userAIPrompt },
        );
        resolve({ data, status });
      } catch (error) {
        reject(error);
      }
    });
  },
};
